import React, { useEffect, useState } from 'react';
import Popper from '@mui/material/Popper';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { API_ENDPOINT } from '../../config/config';
import {
  changeTradeTeam,
  selectDraftConfig,
  setTeamPickIndex,
} from '../../app/features/draftConfig/draftConfigSlice';
import {
  changeTeamInfo,
  choosePick,
  selectTrades,
  setAcceptTrade,
  setChangeTrades,
  setMainTeam,
  setManualTrade,
  setMyTeam,
  setNotAccept,
  setRandomAccepted,
  setRandomFlag,
  setResetSelectTeam,
  teamAction,
  teamAddPicks,
  teamMountAction,
} from '../../app/features/trades/tradesSlice';
import changeTeamPick from '../../components/ModalTrades/modalTradesFunc';
import randomTrade from '../../components/ModalTrades/randomAlgoritm';
import {
  TradePanelBody,
  ColLabel,
  StatusBar,
  TradeColumns,
  TradeCol,
  TradeDivider,
  TeamHeading,
  TeamLogo,
  TeamName,
  TeamDropdownWrap,
  TeamDropdownBtn,
  TeamStaticBox,
  TeamDropdownLogo,
  TeamDropdownLabel,
  DropdownChevron,
  TeamDropdownList,
  TeamDropdownItem,
  YearLabel,
  PickGrid,
  PickTile,
  TradeFooter,
  TradeBtn,
  SecondaryBtn,
} from './TradeModal.styles';

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

// Client rule (2026-09-29, docs/trading.md): the team that ends up with the earliest pick
// in the deal is moving up, and pays a premium on the value it receives — 20% when that pick is in
// round 1, 10% in every other round.
const TRADE_PREMIUM_ROUND_1 = 0.2;
const TRADE_PREMIUM = 0.1;

// Rejected offers the user gets in a draft, across every team — like PFF's limit, but 10.
export const TRADE_ATTEMPTS = 10;

// Full chart values: picks are worth 33.3 or 2.735, and whole numbers skewed the small ones.
const sideValue = (team) =>
  team.pick.reduce((a, i) => a + Number(i.value), 0) +
  team.pickYear.reduce((a, i) => a + Number(i.value), 0) +
  (team.player?.value ?? 0);

const fmt = (n) => Math.round(n * 100) / 100;

// The other team's answer. roundOf(index) gives a pick's round, from the board.
//  - User moving up (they get the earliest pick): they must give at least (1 + premium) × what
//    they get.
//  - User moving down (the other team gets it): the other team pays the premium and no more.
//    Asking for less is a good deal for them, so they accept; asking for more, they reject.
const evaluateTrade = ({ myTeam, mainTeam, roundOf }) => {
  const youGive = sideValue(myTeam);
  const theyGive = sideValue(mainTeam);
  const top = Math.min(...[...myTeam.pick, ...mainTeam.pick].map((p) => p.index));
  const youMoveUp = mainTeam.pick.some((p) => p.index === top);
  const premium = +roundOf(top) === 1 ? TRADE_PREMIUM_ROUND_1 : TRADE_PREMIUM;
  const target = (1 + premium) * (youMoveUp ? theyGive : youGive);
  // A hair of tolerance: 1.1 × 1000 is 1100.0000000000002 in floating point
  const accepted = youMoveUp ? youGive >= target - 1e-9 : theyGive <= target + 1e-9;
  return { accepted, youMoveUp, premium, target, top };
};

// "Jets give #19 (348) #83 (60) = 408". Totals come from sideValue, so the line always matches the
// decision; anything beyond the listed picks (players, future picks — AI offers only) is shown.
const describeSide = (team) => {
  const picksVal = team.pick.reduce((a, i) => a + Number(i.value), 0);
  const extra = fmt(sideValue(team) - picksVal);
  const picks = team.pick.map((p) => `#${p.index} (${Number(p.value)})`).join(' ');
  return `${team.name} give ${picks}${extra ? ` + ${extra} other` : ''} = ${fmt(sideValue(team))}`;
};

// What makes a pick belong to a team on the board. Only these move in a trade — the pick's own
// fields (id, index, value, and player once made) stay with the pick.
const ownerFields = (row) => ({
  round: row.round,
  city: row.city,
  team_neads_info: row.team_neads_info,
});

// v2's own version of getTradesPlayer, which v1 still uses unchanged. That one keeps the team as it
// was before the request (~0.6s) and writes it back afterwards: picks toggled in the meantime were
// cleared, and a team switched away from came back. This reads the team when the players arrive,
// and drops them if it is no longer the one showing.
const loadTeamPlayers = ({ id, path }) => async (dispatch, getState) => {
  try {
    const res = await axios.get(`${API_ENDPOINT}treading/?position=&round=${id}`);
    const team = selectTrades(getState())[path];
    if (team.id !== id) return;
    const players = [{ player: 'Select Players', position: '' }, ...res.data.results];
    dispatch((path === 'myTeam' ? setMyTeam : setMainTeam)({ ...team, players, player: [] }));
  } catch (error) {
    // Players only feed AI offers; a trade of picks goes through without them.
  }
};

// Leaves the trade: DraftBoard watches changeTrades, switches the panel back to Draft a Player and
// resumes the draft. Cancel / Done and clicking the Draft a Player tab all go through here.
export const closeTrade = () => (dispatch, getState) => {
  const { randomFlag } = selectTrades(getState());
  dispatch(setChangeTrades(true));
  dispatch(setManualTrade(false));
  dispatch(setResetSelectTeam());
  // Otherwise the next Trade tab opens on the last trade's "Trade Accepted!" screen, or on a
  // stale "Not Accepted" banner.
  dispatch(setAcceptTrade(false));
  dispatch(setNotAccept(false));
  if (randomFlag) {
    dispatch(setRandomFlag(false));
    dispatch(setRandomAccepted(false));
  }
};

// The Trade tab of the side panel (YourPickModal). It was a modal, hence the name.
// attemptsLeft / onRejected: the draft's attempt count, kept by DraftBoard (this tab unmounts).
const TradeModal = ({ tradesTeams, teamSelect, attemptsLeft = TRADE_ATTEMPTS, onRejected }) => {
  const { teamPickIndex } = useSelector(selectDraftConfig);
  const {
    mainTeam,
    mainTeams,
    myTeam,
    myTeams,
    notAccept,
    acceptTrade,
    historyTrades,
    changeTrades,
    randomFlag,
  } = useSelector(selectTrades);

  const { tradeValue: tradeValueResults } = useSelector(selectDraftConfig);

  // Picks come from the live board, the same rows the simulator reads ownership from. They used to
  // come from trades.tradeValue, which nothing in v2 has loaded since getTrades was dropped in
  // v3.6, so both columns opened empty. A pick is tradeable until it has been made.
  const boardPicks = tradeValueResults.results ?? [];
  const openPicksOf = (teamName) =>
    boardPicks.filter((item) => item.round.name === teamName && !item.player);

  // Teams to trade with: not the user's own, and still holding a pick not yet made — a team with
  // nothing left to offer isn't listed (nor picked as the default partner).
  const otherTeamOptions = tradesTeams.filter(
    (t) => !teamSelect.some((s) => s.name === t.name) && openPicksOf(t.name).length > 0
  );

  const [openFlag, setOpenFlag] = useState(!changeTrades);
  const [randomBool, setRandomBool] = useState(false);
  const [showTeamDrop, setShowTeamDrop] = useState(false);
  const [showMyTeamDrop, setShowMyTeamDrop] = useState(false);
  // The columns scroll (TradeCol has overflow-y: auto), and an absolutely positioned list cannot
  // escape a scrolling ancestor however high its z-index — in the Trade tab it was clipped and
  // the footer drew over it. These anchor a Popper, which portals to the body instead.
  const [teamDropAnchor, setTeamDropAnchor] = useState(null);
  const [myTeamDropAnchor, setMyTeamDropAnchor] = useState(null);

  const dispatch = useDispatch();

  const handleClose = () => {
    setOpenFlag(false);
    dispatch(closeTrade());
  };

  // Mount initial teams
  useEffect(() => {
    if (randomFlag) {
      let loop = true;
      while (loop) {
        const idx = Math.floor(Math.random() * tradesTeams.length);
        if (teamSelect[0].id !== tradesTeams[idx].id) {
          loop = false;
          dispatch(teamMountAction({ team: tradesTeams[idx], path: 'mainTeam' }));
          dispatch(teamMountAction({ team: teamSelect[0], path: 'myTeam' }));
          return;
        }
      }
    }
    // Not tradesTeams[0]: that is whoever holds pick 1, which can be the user's own team. None at
    // all when the user controls every team — the Trade tab is disabled then, but never mount an
    // undefined team (it crashed the render).
    if (otherTeamOptions[0]) dispatch(teamMountAction({ team: otherTeamOptions[0], path: 'mainTeam' }));
    dispatch(teamMountAction({ team: teamSelect[0], path: 'myTeam' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A side's picks: the ones its team still holds and hasn't used — always read from the board. A
  // remembered team (switched away from, or just traded with) keeps its players, but a remembered
  // pick list goes stale the moment a pick is made: a team looked at before the draft still
  // offered picks drafted since.
  const loadSide = (path, team, remembered) => {
    if (!team?.name || !boardPicks.length) return;
    const saved = remembered?.find((item) => item.name === team.name);
    if (saved) dispatch(teamMountAction({ team: saved, path }));
    const info = openPicksOf(team.name);
    const picks = info.map((item) => ({ pick: item.pick, value: item.value, index: item.index }));
    const picksYears = info.map((item) => ({ round: `R${item.round_index_number}`, id: item.id, value: item.value }));
    dispatch(teamAddPicks({ picksInfo: { picks, picksYears, pick: [], pickYear: [] }, path }));
    // Players once per team — asked again if it was switched away from before they came back
    if (!(saved ?? team).players) dispatch(loadTeamPlayers({ id: team.id, path }));
  };

  useEffect(() => {
    loadSide('mainTeam', mainTeam, mainTeams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTeam?.name, tradeValueResults.results]);

  useEffect(() => {
    loadSide('myTeam', myTeam, myTeams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myTeam.name, tradeValueResults.results]);

  const roundOf = (index) => boardPicks.find((p) => p.index === index)?.round_index_number;

  const changeTeam = () => {
    const verdict = evaluateTrade({ myTeam, mainTeam, roundOf });
    const acceptFlag = !randomBool ? verdict.accepted : true;
    const mover = verdict.youMoveUp ? myTeam.name : mainTeam.name;
    const offerLine =
      `[TRADE] ${randomBool ? 'AI offer' : 'offer'} — ${describeSide(myTeam)} | ${describeSide(mainTeam)} | ` +
      `${mover} move up to #${verdict.top} (+${Math.round(verdict.premium * 100)}%): ` +
      (verdict.youMoveUp
        ? `${myTeam.name} must give ≥ ${fmt(verdict.target)}`
        : `${mainTeam.name} pay ≤ ${fmt(verdict.target)}`);
    if (acceptFlag) {
      // Ownership comes from a row each team holds right now — never the whole row, which on the
      // live board can carry a made pick's player, id and grading onto a pick not yet made.
      const teamMainData = ownerFields(boardPicks.find((item) => item.round.name === mainTeam.name));
      const myTeamData = ownerFields(boardPicks.find((item) => item.round.name === myTeam.name));

      const newTradesValue = changeTeamPick({
        teamPick: mainTeam.pick.map((item) => item.index),
        myTeamPick: myTeam.pick.map((item) => item.index),
        tradeValue: boardPicks,
        acceptFlag,
        teamMainData,
        myTeamData,
      });

      // teamPickIndex is the queue of the user's picks not yet made, across all their teams.
      const newTradePickIndex = [
        ...newTradesValue.teamPickIndex,
        ...teamPickIndex.filter((item) => !newTradesValue.myTeamPickIndex.includes(item)),
      ].sort((a, b) => a - b);

      console.log(
        `${offerLine} → ${randomBool ? 'auto-accepted' : 'ACCEPTED'}\n` +
        `[TRADE] applied — ${mainTeam.pick.map((p) => `#${p.index}`).join(' ')} → ${myTeam.name}, ` +
        `${myTeam.pick.map((p) => `#${p.index}`).join(' ')} → ${mainTeam.name} | ` +
        `your picks now: ${newTradePickIndex.join(', ') || 'none'}`
      );

      // changeTeamInfo (shared with v1) spreads each side's players and throws if they have not
      // arrived, which left the board traded and the modal not. A pick trade does not need them.
      if (!myTeam.players) dispatch(setMyTeam({ ...myTeam, players: [], player: [] }));
      if (!mainTeam.players) dispatch(setMainTeam({ ...mainTeam, players: [], player: [] }));

      dispatch(changeTradeTeam(newTradesValue.tradeValue));
      dispatch(setTeamPickIndex(newTradePickIndex));
      dispatch(changeTeamInfo());
      dispatch(setAcceptTrade(acceptFlag));
    } else {
      onRejected?.();
      dispatch(setNotAccept(true));
      console.log(`${offerLine} → REJECTED | ${attemptsLeft - 1} attempts left`);
    }
  };

  const handleTryAgain = () => {
    // No reset of the attempts: they're for the whole draft, so one small accepted trade doesn't
    // hand out 10 fresh ones.
    dispatch(setAcceptTrade(false));
    dispatch(setNotAccept(false));
  };

  useEffect(() => {
    if (
      Object.keys(mainTeam).length >= 12 &&
      Object.keys(myTeam).length >= 12 &&
      myTeam.pick.length === 0 &&
      randomFlag
    ) {
      const randomTeam = randomTrade({ mainTeam, myTeam });
      dispatch(setMyTeam(randomTeam[0].myTeam));
      dispatch(setMainTeam(randomTeam[0].mainTeam));
      setRandomBool(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myTeam, mainTeam]);

  useEffect(() => {
    if (!showTeamDrop) return;
    const handler = () => setShowTeamDrop(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showTeamDrop]);

  useEffect(() => {
    if (!showMyTeamDrop) return;
    const handler = () => setShowMyTeamDrop(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showMyTeamDrop]);

  const mySelectedIndices = myTeam.pick?.map((p) => p.index) ?? [];
  const mainSelectedIndices = mainTeam.pick?.map((p) => p.index) ?? [];
  const canTrade = mySelectedIndices.length > 0 && mainSelectedIndices.length > 0;
  // All 10 attempts used this draft: the tab still opens, but everything in it is disabled.
  const outOfAttempts = attemptsLeft <= 0;

  return (
    <>
      {openFlag && (
        <TradePanelBody>
            {notAccept && !acceptTrade && attemptsLeft > 0 && (
              <StatusBar $accepted={false}>
                <strong>Trade Not Accepted</strong> ({attemptsLeft} attempt{attemptsLeft === 1 ? '' : 's'} remaining)
              </StatusBar>
            )}
            {!acceptTrade && outOfAttempts && (
              <StatusBar $accepted={false}>
                <strong>Trade Not Accepted</strong> (No attempts remaining)
              </StatusBar>
            )}
            {acceptTrade && (
              <StatusBar $accepted={true}>
                <strong>Trade Accepted</strong>
              </StatusBar>
            )}

            <TradeColumns $disabled={outOfAttempts}>
              {/* ── LEFT: Other team ───────────────────────────── */}
              <TradeCol>
                {!acceptTrade && <ColLabel>Select a team to trade with</ColLabel>}
                {!acceptTrade ? (
                  <TeamDropdownWrap onClick={(e) => e.stopPropagation()}>
                    <TeamDropdownBtn
                      type="button"
                      ref={setTeamDropAnchor}
                      onClick={() => setShowTeamDrop((p) => !p)}
                    >
                      {mainTeam.logo && <TeamDropdownLogo src={mainTeam.logo} alt={mainTeam.name} />}
                      <TeamDropdownLabel>{mainTeam.name || 'Select team'}</TeamDropdownLabel>
                      <DropdownChevron><ChevronDown /></DropdownChevron>
                    </TeamDropdownBtn>
                    {showTeamDrop && (
                      <Popper
                        open
                        anchorEl={teamDropAnchor}
                        placement="bottom-start"
                        style={{ zIndex: 1400, width: teamDropAnchor?.offsetWidth }}
                      >
                      <TeamDropdownList>
                        {otherTeamOptions.map((t) => (
                          <TeamDropdownItem
                            key={t.name}
                            $active={t.name === mainTeam.name}
                            onClick={() => {
                              setShowTeamDrop(false);
                              // Re-picking the team already shown swapped in the bare team and
                              // emptied its picks — they only rebuild when the name changes.
                              if (t.name === mainTeam.name) return;
                              dispatch(teamAction({ team: t, path: 'mainTeam' }));
                              dispatch(setNotAccept(false));
                            }}
                          >
                            <img src={t.logo} alt={t.name} />
                            <span>{t.name}</span>
                          </TeamDropdownItem>
                        ))}
                      </TeamDropdownList>
                      </Popper>
                    )}
                  </TeamDropdownWrap>
                ) : (
                  <TeamHeading>
                    {mainTeam.logo && <TeamLogo src={mainTeam.logo} alt={mainTeam.name} />}
                    <TeamName>{mainTeam.name || '—'}</TeamName>
                  </TeamHeading>
                )}

                {!acceptTrade ? (
                  <>
                    <YearLabel>2026</YearLabel>
                    <PickGrid>
                      {mainTeam.picks?.map((item) => (
                        <PickTile
                          key={item.index}
                          $active={mainSelectedIndices.includes(item.index)}
                          onClick={() =>
                            dispatch(choosePick({ pick: item, path: 'mainTeam', pickPath: 'pick' }))
                          }
                        >
                          {item.index}
                        </PickTile>
                      ))}
                    </PickGrid>
                  </>
                ) : (
                  <>
                    <YearLabel>Received</YearLabel>
                    <PickGrid>
                      {historyTrades.slice(-1)[0]?.mainTeam?.pick?.map((p) => (
                        <PickTile key={p.index} $active>
                          {p.index}
                        </PickTile>
                      ))}
                    </PickGrid>
                  </>
                )}
              </TradeCol>

              <TradeDivider />

              {/* ── RIGHT: My team ────────────────────────────── */}
              <TradeCol>
                {!acceptTrade && (
                  <ColLabel>{teamSelect.length > 1 ? 'Select one of your teams' : 'Your team'}</ColLabel>
                )}
                {/* Which of the user's teams is trading. Was fixed to teamSelect[0], so with two
                    teams selected you could only ever trade as the first one. */}
                {!acceptTrade && teamSelect.length > 1 ? (
                  <TeamDropdownWrap onClick={(e) => e.stopPropagation()}>
                    <TeamDropdownBtn
                      type="button"
                      ref={setMyTeamDropAnchor}
                      onClick={() => setShowMyTeamDrop((p) => !p)}
                    >
                      {myTeam.logo && <TeamDropdownLogo src={myTeam.logo} alt={myTeam.name} />}
                      <TeamDropdownLabel>{myTeam.name || 'Select team'}</TeamDropdownLabel>
                      <DropdownChevron><ChevronDown /></DropdownChevron>
                    </TeamDropdownBtn>
                    {showMyTeamDrop && (
                      <Popper
                        open
                        anchorEl={myTeamDropAnchor}
                        placement="bottom-start"
                        style={{ zIndex: 1400, width: myTeamDropAnchor?.offsetWidth }}
                      >
                      <TeamDropdownList>
                        {teamSelect.map((t) => (
                          <TeamDropdownItem
                            key={t.name}
                            $active={t.name === myTeam.name}
                            onClick={() => {
                              setShowMyTeamDrop(false);
                              if (t.name === myTeam.name) return; // same as the partner list
                              dispatch(teamAction({ team: t, path: 'myTeam' }));
                            }}
                          >
                            <img src={t.logo} alt={t.name} />
                            <span>{t.name}</span>
                          </TeamDropdownItem>
                        ))}
                      </TeamDropdownList>
                      </Popper>
                    )}
                  </TeamDropdownWrap>
                ) : !acceptTrade ? (
                  <TeamStaticBox as="div">
                    {myTeam.logo && <TeamDropdownLogo src={myTeam.logo} alt={myTeam.name} />}
                    <TeamDropdownLabel>{myTeam.name || '—'}</TeamDropdownLabel>
                  </TeamStaticBox>
                ) : (
                  <TeamHeading>
                    {myTeam.logo && <TeamLogo src={myTeam.logo} alt={myTeam.name} />}
                    <TeamName>{myTeam.name || '—'}</TeamName>
                  </TeamHeading>
                )}

                {!acceptTrade ? (
                  <>
                    <YearLabel>2026</YearLabel>
                    <PickGrid>
                      {myTeam.picks?.map((item) => (
                        <PickTile
                          key={item.index}
                          $active={mySelectedIndices.includes(item.index)}
                          onClick={() =>
                            dispatch(choosePick({ pick: item, path: 'myTeam', pickPath: 'pick' }))
                          }
                        >
                          {item.index}
                        </PickTile>
                      ))}
                    </PickGrid>
                  </>
                ) : (
                  <>
                    <YearLabel>Received</YearLabel>
                    <PickGrid>
                      {historyTrades.slice(-1)[0]?.myTeam?.pick?.map((p) => (
                        <PickTile key={p.index} $active>
                          {p.index}
                        </PickTile>
                      ))}
                    </PickGrid>
                  </>
                )}
              </TradeCol>
            </TradeColumns>

            {/* Nothing to show once the attempts are used up, so no empty footer strip */}
            {(acceptTrade || !outOfAttempts) && (
              <TradeFooter>
                {acceptTrade ? (
                  <>
                    <SecondaryBtn onClick={handleTryAgain}>Trade Again</SecondaryBtn>
                    <TradeBtn onClick={handleClose}>
                      {randomFlag ? 'Accept' : 'Done'}
                    </TradeBtn>
                  </>
                ) : (
                  <>
                    {/* No Cancel: the Draft a Player tab leaves the trade (same closeTrade). */}
                    {!outOfAttempts && (
                      <TradeBtn $large disabled={!canTrade} onClick={changeTeam}>
                        {randomFlag ? 'Accept' : 'Offer Trade'}
                      </TradeBtn>
                    )}
                  </>
                )}
              </TradeFooter>
            )}
        </TradePanelBody>
      )}
    </>
  );
};

export default TradeModal;
